"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.loggingStateServiceKey = exports.LoggingStateService = exports.LogActionId = exports.ContextKey = exports.LogLevel = void 0;
const cufflink_1 = require("@smartsheet/cufflink");
const LogService_1 = require("./LogService");
const LogRequestBuilder_1 = require("./LogRequestBuilder");
const error_stack_parser_1 = __importDefault(require("error-stack-parser"));
/**
 * These are set to match the app-core ClientLogger and GlobalLogManager.
 */
var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["DEBUG"] = 1] = "DEBUG";
    LogLevel[LogLevel["INFO"] = 2] = "INFO";
    LogLevel[LogLevel["WARN"] = 3] = "WARN";
    LogLevel[LogLevel["ERROR"] = 4] = "ERROR";
})(LogLevel = exports.LogLevel || (exports.LogLevel = {}));
/** a collection of standard keys used to identify parameters passed with the application-log data */
var ContextKey;
(function (ContextKey) {
    ContextKey[ContextKey["featureArea"] = 0] = "featureArea";
    ContextKey[ContextKey["gridID"] = 1] = "gridID";
    ContextKey[ContextKey["organizationID"] = 2] = "organizationID";
    ContextKey[ContextKey["pillar"] = 3] = "pillar";
    ContextKey[ContextKey["requestName"] = 4] = "requestName";
    ContextKey[ContextKey["requestUri"] = 5] = "requestUri";
    ContextKey[ContextKey["userAccountType"] = 6] = "userAccountType";
    ContextKey[ContextKey["userDataSource"] = 7] = "userDataSource";
    ContextKey[ContextKey["userID"] = 8] = "userID";
    ContextKey[ContextKey["userLocale"] = 9] = "userLocale";
    ContextKey[ContextKey["userTimeZone"] = 10] = "userTimeZone";
})(ContextKey = exports.ContextKey || (exports.ContextKey = {}));
/**
 * The values in this enum number should map to an entry in ClientEventLookup.java in app-core.
 * Action IDs are in the range 0-99.
 * Note, this is for BI data only - it is not used for application-logs.
 *
 * todo: generate and publish from client event lookup.
 */
var LogActionId;
(function (LogActionId) {
    LogActionId[LogActionId["OPEN"] = 1] = "OPEN";
    LogActionId[LogActionId["CLOSE"] = 2] = "CLOSE";
    LogActionId[LogActionId["DRAG"] = 5] = "DRAG";
    LogActionId[LogActionId["SUBMIT"] = 15] = "SUBMIT";
    LogActionId[LogActionId["EXECUTE"] = 16] = "EXECUTE";
    LogActionId[LogActionId["MOUSE_DOWN"] = 20] = "MOUSE_DOWN";
    LogActionId[LogActionId["MOUSE_UP"] = 21] = "MOUSE_UP";
    LogActionId[LogActionId["CLICK"] = 22] = "CLICK";
    LogActionId[LogActionId["DOUBLE_CLICK"] = 23] = "DOUBLE_CLICK";
    LogActionId[LogActionId["CONTEXT_CLICK"] = 24] = "CONTEXT_CLICK";
    LogActionId[LogActionId["MIDDLE_CLICK"] = 29] = "MIDDLE_CLICK";
    LogActionId[LogActionId["SHORTCUT_KEY"] = 31] = "SHORTCUT_KEY";
    LogActionId[LogActionId["KEY_STROKE"] = 32] = "KEY_STROKE";
    LogActionId[LogActionId["SELECT"] = 51] = "SELECT";
    LogActionId[LogActionId["COMPLETE"] = 53] = "COMPLETE";
    LogActionId[LogActionId["CANCEL"] = 54] = "CANCEL";
    LogActionId[LogActionId["ERROR"] = 55] = "ERROR";
    LogActionId[LogActionId["DIAGNOSTIC"] = 99] = "DIAGNOSTIC";
})(LogActionId = exports.LogActionId || (exports.LogActionId = {}));
/**
 * This service concerns itself with communicating log data to the server.
 *
 * warn/error/log are used to send application logs
 * logClientEvent/logEventWithoutPrivateInfo are used to send BI logs
 */
class LoggingStateService {
    // Constructor creates a LegacyLogger to handle BI logs, and a new LogServiceClient singleton to pass logs to the logging-service Lambda
    constructor(proxyLogger, logService = LogService_1.LogService.instance()) {
        var _a;
        this.proxyLogger = proxyLogger;
        this.logService = logService;
        this.appName = "unknown-app";
        this.appVersion = (_a = window.clientState) === null || _a === void 0 ? void 0 : _a.version; // clientState is optional so we have a fallback in case it's not available
        this.featureArea = "unknown-feature";
        this.warn = (message, exception, contextMap, appName = this.appName, appVersion = this.appVersion, featureArea = this.featureArea, fileName, lineNumber) => {
            if (exception !== undefined) {
                fileName = error_stack_parser_1.default.parse(exception)[0].fileName;
                lineNumber = error_stack_parser_1.default.parse(exception)[0].lineNumber;
            }
            this.log(LogLevel.WARN, message, exception, contextMap, appName, appVersion, featureArea, fileName, lineNumber);
        };
        this.error = (message, exception, contextMap, appName = this.appName, appVersion = this.appVersion, featureArea = this.featureArea, fileName, lineNumber) => {
            if (exception !== undefined) {
                fileName = error_stack_parser_1.default.parse(exception)[0].fileName;
                lineNumber = error_stack_parser_1.default.parse(exception)[0].lineNumber;
            }
            this.log(LogLevel.ERROR, message, exception, contextMap, appName, appVersion, featureArea, fileName, lineNumber);
        };
        this.log = (logLevel, message, exception, contextMap, appName = this.appName, appVersion = this.appVersion, featureArea = this.featureArea, fileName, lineNumber) => {
            const requestBuilder = new LogRequestBuilder_1.LogRequestBuilder();
            if (contextMap !== undefined) {
                for (const [key, value] of contextMap) {
                    requestBuilder.addContext(key, value);
                }
            }
            if (exception !== undefined) {
                fileName = error_stack_parser_1.default.parse(exception)[0].fileName;
                lineNumber = error_stack_parser_1.default.parse(exception)[0].lineNumber;
            }
            const request = requestBuilder
                .logLevel(logLevel)
                .messageTemplate(message)
                .stack(exception === null || exception === void 0 ? void 0 : exception.stack)
                .appName(appName)
                .appVersion(appVersion)
                .featureArea(featureArea)
                .fileName(fileName)
                .lineNumber(lineNumber)
                .build();
            this.logService.push(request);
        };
        this.logClientEvent = (controlType, controlId, actionId) => {
            this.proxyLogger.logClientEvent(controlType, controlId, actionId);
        };
        this.logEventWithoutPrivateInfo = (objectID, actionID, parm1String, parm1Int, eventMetadata) => {
            this.proxyLogger.logEventWithoutPrivateInfo(objectID, actionID, parm1String, parm1Int, eventMetadata);
        };
    }
}
exports.LoggingStateService = LoggingStateService;
exports.loggingStateServiceKey = cufflink_1.getKey("loggingStateService", LoggingStateService);
//# sourceMappingURL=LoggingStateService.js.map